// imports
import { AnyAction } from '@reduxjs/toolkit';
import { useSelector, useDispatch } from 'react-redux';
import React, { useRef, useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  IModal, Quest, QuestCost, QuestStatus
} from '../../common/types';
import { ROUTES } from '../../constants';
import GameLayout from '../../layouts/game';
import TabView from '../../molecules/tabView';
import { QUEST_UTILS } from '../../common/utils';
import QuestList from '../../molecules/list/quest';
import RewardsPrompt from '../../molecules/prompt/rewards';
import QuestDetailView from '../../molecules/tabView/sidebar/questDetailView';
import {
  selectConfig,
  selectUIMap, selectUIPrompt,
  selectUserBackpack, selectUserQuests, selectUserHeroes
} from '../../repositories/store/slices';
import { fetchHeroes } from '../../repositories/store/thunks';
import { setCrewTraitMatching } from '../../repositories/store/actions';

// exports
export default function Quests(): JSX.Element {
  // hooks configuration
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { state } = useLocation();
  const rewardsPromptRef = useRef<IModal>();
  const { world } = useSelector(selectConfig);
  const { items } = useSelector(selectUserBackpack);
  const { data: questsData } = useSelector(selectUserQuests);
  const { reward: { awarded } } = useSelector(selectUIPrompt);
  const { currRegion: { id: currRegionId } } = useSelector(selectUIMap);
  const { nfts: heroNFTs, fetchStatus: fetchHeroesStatus } = useSelector(selectUserHeroes);
  const [selectedQuest, setSelectedQuest] = useState<Quest | undefined>(questsData[state?.lastSelectedQuestId]);
  // componentDidMount
  useEffect(() => {
    if (awarded.length) { rewardsPromptRef.current?.show(); }
    if (fetchHeroesStatus === 'idle') { dispatch(fetchHeroes() as unknown as AnyAction); }
  });
  useEffect(() => {
    if (selectedQuest && !selectedQuest.crewTraitMatching?.length && heroNFTs.length) {
      const [, questTM] = QUEST_UTILS.getQuestTraitMatching(selectedQuest.traits, selectedQuest.crew, heroNFTs);
      if (questTM.length) {
        setSelectedQuest({ ...selectedQuest, crewTraitMatching: questTM });
        dispatch(setCrewTraitMatching({ questId: selectedQuest.id, crewTraitMatching: questTM }));
      }
    }
  }, [dispatch, heroNFTs, selectedQuest]);
  // private methods
  const questClickedCB = (pSelectedQuest?: Quest): void => setSelectedQuest(pSelectedQuest);
  const insufficientResources: QuestCost[] = [];
  if (selectedQuest && selectedQuest.status === QuestStatus.available) {
    selectedQuest.cost?.forEach((cost) => {
      const itemCount = items[cost.itemId] || 0;
      if (itemCount < cost.amount) { insufficientResources.push(cost); }
    });
  }
  const navigateBack = (): void => navigate(ROUTES.GAME);
  const currRegion = world.regions ? world.regions[currRegionId] : { name: '', description: '' };
  const placeHolderRegion = (state?.pinId && world.pins && world.pins[state.pinId]) ? world.islands[world.pins[state.pinId].islandId] : currRegion;
  const questDetailView = (
    <QuestDetailView
      quest={selectedQuest}
      placeholder={placeHolderRegion}
      crew={selectedQuest?.crew || [0, 0, 0]}
      insufficientResources={insufficientResources}
      questTraitMatching={selectedQuest?.crewTraitMatching}
    />
  );
  let headerItems: string[] = [];
  switch (selectedQuest?.status) {
    case QuestStatus.available:
      headerItems = selectedQuest.cost.map((cost) => cost.itemId);
      break;
    case QuestStatus.completed:
      headerItems = selectedQuest.rewards.map((reward) => reward.itemId);
      break;
    default:
      headerItems = [];
  }
  // render
  return (
    <>
      <RewardsPrompt ref={rewardsPromptRef} />
      <GameLayout
        backBtnClickedCB={navigateBack}
        items={headerItems.length ? headerItems : undefined}
        backButtonText={currRegionId ? world.regions[currRegionId].name : ''}
      >
        <TabView sidebar={questDetailView}>
          <QuestList
            pinId={state?.pinId}
            selectedQuest={selectedQuest}
            defaultTab={state?.defaultTab}
            questClickedCB={questClickedCB}
            filterChangedCB={(quest?: Quest) => { questClickedCB(quest); }}
          />
        </TabView>
      </GameLayout>
    </>
  );
}
